import { GuidHelper } from "@/Common/Helpers/GuidHelper";
import { ValidationHelper } from "@/Common/Helpers/ValidationHelper";
import { LocalEvent } from "@/Common/Stores/Actions/LocalEvent";

interface ITextboxMaskedProps {
    onChangeOperation?: number;
    onChangeFunction?: (unmaskedValue: string, maskedvalue: string) => void;
    onBlurFunction?: (unmaskedValue: string, maskedvalue: string) => void;
    onKeyDown?: (event: React.KeyboardEvent<HTMLInputElement>) => void;

    alphanumericIncludesPeriod?: boolean; // determines if * mask character includes periods

    isRequired?: boolean;

    id?: string;                          // unique ID
    dataCy?: string;
    value: string;
    mask: string;                         // mask string see https://github.com/RobinHerbots/Inputmask for formats
    isValid: boolean;                     // will set the control style to invalid/valid
    validationRegexPattern?: string;      //  validation regex sent to onChangeOperation to do preliminary regex-style validation
    extraValidationFunction?: () => void; // function that will be called by onChangeOperation to do

    debounceMs?: number;
    validationErrorMessage?: string;
    returnMaskedValue?: boolean;          // toggle to return value including mask
    allowEmpty?: boolean;
    item?: any;                           // object that is being validated
    style?: any                           // extra style
    disabled?: boolean;
    isEmail?: boolean;                    // special case when used for an email address input
    focus?: boolean;
    placeholder?: string;
    className?: string;
    maxLength?: number;
}

class TextboxMaskedControl extends React.Component<ITextboxMaskedProps, never> {
    private id: string;
    private periodAlphanumericsDefinition = {
        "*": {
            validator: "[-_0-9A-Za-z.]", // added -_ but this might break other validations.. component api not flexible and must be rethought.
            cardinality: 1,
            casing: "lower"
        }
    };

    private inputRef: React.RefObject<HTMLInputElement> = React.createRef();

    private debounceCallbackHandle?: number;

    constructor(props: ITextboxMaskedProps) {
        super(props);

        $("#" + this.id).unbind("input"); // cleanup

        if (this.props.id == null) {
            this.id = GuidHelper.NewGuid();
        }
        else {
            this.id = this.props.id;
        }
    }

    public render(): JSX.Element {
        var errorClass = this.props.isValid ? "" : " has-error";
        // allow empty by default
        var allowEmpty = true;
        if (this.props.allowEmpty == null || this.props.allowEmpty === undefined)
            allowEmpty = true;
        else
            allowEmpty = this.props.allowEmpty;

        var hasValue = !(this.props.value == null || this.props.value.length == 0);

        if (allowEmpty && !hasValue)
            errorClass = "";

        let placeHolder = "";
        if (this.props.placeholder != null)
            placeHolder = this.props.placeholder;

        return (
            <div className={"form-group" + errorClass}>

                {this.props.isRequired && !hasValue && <span>*</span>}

                <input
                    maxLength={this.props.maxLength}
                    style={this.props.style}
                    className={"form-control " + this.props.className}
                    id={this.id}
                    data-cy={this.props.dataCy}
                    key={this.id}
                    placeholder={placeHolder}
                    onKeyDown={event => this.props.onKeyDown?.(event)}
                    type="text"
                    disabled={this.props.disabled === true}
                    value={this.props.value}
                    onBlur={this.onBlur.bind(this)}
                    onChange={event => this.onChange(event.target.value)}
                    ref={this.inputRef}
                />
                {!this.props.isValid && this.props.value && this.props.value.length > 0 && this.props.validationErrorMessage != null && this.props.validationErrorMessage.length > 0 &&
                    <label htmlFor={this.props.id} className="label-error"> {this.props.validationErrorMessage} </label>
                }
            </div>
        );
    }

    public onChange(val: string): void {
        // Marked Complete will not register on Empty String Change. Add This function to cover the case where the new onChange Value is an Empty String
        if (val == "")
            this.Callback("", "");

        const { unMaskedValue, maskedValue } = this.getValues();
        this.props.onChangeFunction?.(unMaskedValue, maskedValue);
    }

    public onBlur(): void {
        const { unMaskedValue, maskedValue } = this.getValues();
        this.props.onBlurFunction?.(unMaskedValue, maskedValue);
    }

    public componentWillUnmount(): void {
        $("#" + this.id).inputmask("remove");

        if (this.debounceCallbackHandle)
            clearTimeout(this.debounceCallbackHandle);
    }

    public resetInputMask(): void {
        var self = this;
        // scrap old mask, rebuild it with new mask
        $("#" + this.id).inputmask("remove");
        var newmask = self.props.mask == null ? "" : self.props.mask;
        var placeHolder = "_";

        if (newmask.trim().length <= 0) {
            // no mask... so lets assume 100char alphanumeric
            newmask = "[***************************************************************************************************]";
            placeHolder = " ";
        }

        if (this.props.isEmail) {
            $("#" + this.id).inputmask({
                mask: ValidationHelper.GetEmailEditMaskPattern(),
                greedy: false,
                definitions: {
                    "*": {
                        validator: "[0-9A-Za-z!#$%&'*+/=?^_`{|}~-]",
                        cardinality: 1,
                        casing: "lower"
                    },
                    "-": {
                        validator: "[0-9A-Za-z-]",
                        cardinality: 1,
                        casing: "lower"
                    }
                },
                isComplete: self.onMaskedValueChanged.bind(self),
                oncleared: () => this.onClear()
            });
        }
        else {
            $("#" + this.id).inputmask({
                mask: newmask,
                placeholder: placeHolder,
                greedy: false,
                skipOptionalPartCharacter: " ",
                isComplete: self.onMaskedValueChanged.bind(self), // use inputmask's isComplete event that is equivalent to an input's onChange event... gets called everytime the value is changed
                definitions: this.props.alphanumericIncludesPeriod ? this.periodAlphanumericsDefinition : {},
                oncleared: () => this.onClear()
            });
        }
    }

    public componentDidMount(): void {
        var newMask = (this.props.mask == null) ? "" : this.props.mask;
        var placeHolder = "_";
        if (newMask.trim().length <= 0) {
            // no mask... asume 100char alphanumeric
            newMask = "*[***************************************************************************************************]";
            placeHolder = " ";
        }

        if (this.props.isEmail) {
            $("#" + this.id).inputmask({
                mask: ValidationHelper.GetEmailEditMaskPattern(),
                greedy: false,
                definitions: {
                    "*": {
                        validator: "[0-9A-Za-z!#$%&'*+/=?^_`{|}~-]",
                        cardinality: 1,
                        casing: "lower"
                    },
                    "-": {
                        validator: "[0-9A-Za-z-]",
                        cardinality: 1,
                        casing: "lower"
                    }
                },
                isComplete: this.onMaskedValueChanged.bind(this),
                oncleared: () => this.onClear()
            });
        }
        else {
            $("#" + this.id).inputmask({
                mask: newMask,
                placeholder: placeHolder,
                greedy: false,
                skipOptionalPartCharacter: " ",
                isComplete: this.onMaskedValueChanged.bind(this), // use inputmask's isComplete event that is equivalent to an input's onChange event... gets called everytime the value is changed
                definitions: this.props.alphanumericIncludesPeriod ? this.periodAlphanumericsDefinition : {},
                oncleared: () => this.onClear()
            });
        }

        if (this.props.focus) {
            this.focus();
        }
    }

    public componentDidUpdate(prevProps: ITextboxMaskedProps): void {
        if ((this.props.mask != prevProps.mask) || (this.props.id != prevProps.id)) {
            this.resetInputMask();
        }

        if (this.props.focus && !prevProps.focus) {
            this.focus();
        }
    }

    private focus(): void {
        this.inputRef.current?.focus();
    }

    private onMaskedValueChanged(): boolean {
        const { unMaskedValue, maskedValue } = this.getValues();
        this.Callback(unMaskedValue, maskedValue);
        return false;
    }

    private getValues(): { unMaskedValue: string, maskedValue: string } {
        let unMaskedValue: string = $("#" + this.id).inputmask("unmaskedvalue");
        let maskedValue = $("#" + this.id).val();

        if (this.props.returnMaskedValue) {
            unMaskedValue = maskedValue;
        }

        return { unMaskedValue, maskedValue };
    }

    private Callback(unmaskedValue: string, maskedValue: string): void {
        if (this.debounceCallbackHandle)
            clearTimeout(this.debounceCallbackHandle);

        var self = this;
        this.debounceCallbackHandle = window.setTimeout(
            () => {
                if (self.props.onChangeOperation) {
                    var operation = new LocalEvent(self.props.onChangeOperation);
                    operation.AddItem("item", self.props.item);
                    operation.AddItem("value", unmaskedValue);
                    operation.AddItem("id", self.id);
                    operation.AddItem("validationRegex", self.props.validationRegexPattern);
                    operation.AddItem("validationFunction", self.props.extraValidationFunction);
                    LocalEventDispatcher.dispatch(operation);
                }

                if (self.props.onChangeFunction) {
                    self.props.onChangeFunction(unmaskedValue, maskedValue);
                }
            },
            this.props.debounceMs || 0
        );
    }

    private onClear(): void {
        this.Callback("", "");
        if (this.props.focus) {
            this.focus();
        }
    }
}

export { TextboxMaskedControl };
